import { LayoutAuth } from "#/layouts/layout.auth";
import { validateAndRouteInviteFn } from "#/modules/generic.invite/invite.functions";
import { buildLocaleHref } from "#/modules/util.helpers/route-href";
import { m } from "#/paraglide/messages";
import { Alert, AlertDescription, AlertTitle, Button, Card, CardContent } from "@beweegbaas/ui";
import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import MaterialSymbolsErrorOutline from "~icons/material-symbols/error-outline";
import { z } from "zod/v4";

export const Route = createFileRoute("/$locale/{-$schoolyear}/invite")({
  validateSearch: z.object({
    inviteId: z.string().catch(""),
    token: z.string().catch(""),
  }),
  beforeLoad: ({ search, params }) => {
    if (!search.inviteId || !search.token) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  loaderDeps: ({ search }) => ({ inviteId: search.inviteId, token: search.token }),
  loader: async ({ params, deps, location }) => {
    // `/invite/new` and `/invite/existing` are nested UNDER this route in the generated tree,
    // so this loader also runs when navigating to them. Only the exact `/invite` path should
    // validate + route; without this guard the parent loader re-validates and re-redirects on
    // every child navigation, producing an infinite redirect loop. Children render via <Outlet/>.
    if (!location.pathname.endsWith("/invite")) {
      return { valid: true as const };
    }

    const decision = await validateAndRouteInviteFn({
      data: { inviteId: deps.inviteId, token: deps.token },
    });

    const locale = params.locale;
    const schoolyear = params.schoolyear;

    if (decision.action === "error") {
      // Return data — the component will render the error card
      return { valid: false as const, error: decision.error };
    }

    if (decision.action === "retry") {
      // The mismatched session was just cleared server-side; redirect back to the same
      // invite URL so the flow restarts unauthenticated.
      throw redirect({
        to: "/$locale/{-$schoolyear}/invite",
        params: { locale, schoolyear },
        search: { inviteId: deps.inviteId, token: deps.token },
      });
    }

    if (decision.action === "redirect-login") {
      // Encode the invite query when building the referer: the DataProtector token contains
      // "+" and "/", which must be percent-encoded so they survive the login round-trip
      // (a raw "+" in a query decodes back to a space, corrupting the token -> InviteInvalid).
      const inviteQuery = new URLSearchParams({
        inviteId: decision.inviteId,
        token: decision.token,
      }).toString();
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale, schoolyear },
        search: {
          email: decision.inviteeEmail,
          schoolName: decision.schoolName,
          referer: buildLocaleHref(`/invite?${inviteQuery}`, { locale, schoolyear }),
          type: "invite",
        },
      });
    }

    if (decision.action === "redirect-existing") {
      throw redirect({
        to: "/$locale/{-$schoolyear}/invite/existing",
        params: { locale, schoolyear },
        search: {
          inviteId: decision.inviteId,
          token: decision.token,
          schoolName: decision.schoolName,
          inviteeEmail: decision.inviteeEmail,
        },
      });
    }

    // redirect-new
    throw redirect({
      to: "/$locale/{-$schoolyear}/invite/new",
      params: { locale, schoolyear },
      search: {
        inviteId: decision.inviteId,
        token: decision.token,
        schoolName: decision.schoolName,
        inviteeEmail: decision.inviteeEmail,
        inviteType: decision.inviteType,
      },
    });
  },
  component: InvitePage,
});

/**
 * Renders the child accept routes (/invite/new, /invite/existing) via <Outlet/>, or the error
 * card when validation of the exact /invite path failed (all success paths throw redirects in
 * the loader, so an "error" in loaderData is the only non-child case that reaches here).
 */
function InvitePage() {
  const loaderData = Route.useLoaderData();
  const params = Route.useParams();

  if (!loaderData || !("error" in loaderData)) {
    return <Outlet />;
  }

  return (
    <LayoutAuth showVisuals={false}>
      <Card className="mb-[20%] w-full max-w-lg">
        <CardContent>
          <Alert variant="destructive" className="">
            <MaterialSymbolsErrorOutline className="text-red-500" />
            <AlertTitle>{m["common.errors.title"]()}</AlertTitle>
            <AlertDescription>
              {loaderData && "error" in loaderData
                ? loaderData.error
                : m["common.something_went_wrong"]()}
            </AlertDescription>
          </Alert>
          <Button
            variant="secondary"
            className="mt-6 w-full"
            render={
              <Link
                to="/$locale/{-$schoolyear}/login"
                params={{ locale: params.locale, schoolyear: params.schoolyear }}
              />
            }
            nativeButton={false}
          >
            {m["common.auth.ga_naar_inloggen"]()}
          </Button>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
